import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import { cn } from '@/shared/ui'
import { focusRing } from '@/shared/ui/cn'
import { ArrowDown, ArrowUp, UnfoldMore } from '@/shared/ui/icons'

import type { SortState } from './search-state'

/**
 * A column header that sorts the results by its field, from the mockups' `find-search/SortableTh.jsx`.
 * Each press moves it a step: ascending, descending, then not sorted. A sorted column shows a filled
 * pill with its arrow and, on a screen that sorts by several columns at once, its place in the
 * order; the others show a two-way arrow. Only the first sorted column carries `aria-sort`, as ARIA
 * asks of one header at a time; each button's name says its own place.
 */
export function SortableHeader(props: {
  readonly label: string
  /** The API sort field. */
  readonly field: string
  /** The order the viewer chose, highest priority first, which may be by other columns; none for the screen's own. */
  readonly sorts: readonly SortState[]
  /** Called with the field when the header is pressed. */
  readonly onSort: (field: string) => void
  /** Shows the column's place in the order, on a screen that sorts by several columns at once. */
  readonly showsPlace?: boolean
  readonly className?: string
}) {
  const intl = useIntl()
  const { field, sorts, onSort } = props
  const at = sorts.findIndex((sort) => sort.field === field)
  const sort = sorts[at]
  const descending = sort?.descending === true
  const place = props.showsPlace === true && sort !== undefined ? at + 1 : null
  const step = useCallback(() => onSort(field), [field, onSort])
  const state = sort === undefined ? 'none' : descending ? 'descending' : 'ascending'

  return (
    // Named by its column alone: the button's name, which says how it sorts, would otherwise become the header's.
    <th scope="col" aria-label={props.label} aria-sort={at === 0 ? state : undefined} className={props.className}>
      <button
        type="button"
        onClick={step}
        // Starts with all it shows, as "Status 2 of 3, sorted descending" does, so speech input finds it (WCAG 2.5.3).
        aria-label={intl.formatMessage(
          { id: place === null ? `search.sort.state.${state}` : `search.sort.state.${state}Place` },
          { column: props.label, place, count: sorts.length },
        )}
        title={intl.formatMessage({ id: `search.sort.next.${state}` })}
        className={cn('inline-flex min-h-6 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded transition hover:text-primary-main', focusRing)}
      >
        {props.label}
        {sort === undefined ? (
          // A token color, not an opacity, so the idle arrow keeps its tested contrast on every surface.
          <UnfoldMore size={18} className="text-text-secondary" />
        ) : (
          // The place is drawn from data-place rather than written as text: checkers that join the button's
          // text with no space would read "Status2", which no name says, though the name holds "Status 2".
          <span
            data-place={place === null ? undefined : intl.formatNumber(place)}
            className={cn(
              'inline-flex h-5 items-center gap-0.5 rounded-full bg-primary-main px-1 text-xs font-semibold text-primary-contrast',
              place !== null && 'pr-1.5 after:content-[attr(data-place)]',
            )}
          >
            {descending ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
          </span>
        )}
      </button>
    </th>
  )
}
